import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  MagnifyingGlass,
  Brain,
  Pulse,
  SignOut,
  Sparkle,
  Gear,
  Stack,
  Trophy,
  ChartLineUp,
  Info,
  Lightning,
  Wallet,
  List,
  X,
  HouseSimple,
  CurrencyBtc,
  Bank,
  Globe,
  Cpu,
  Confetti,
  IdentificationCard,
  Crown,
  Robot,
} from "@phosphor-icons/react";
import { useAuth } from "../auth/AuthProvider";
import AlertsBell from "./AlertsBell";
import ConnectWalletButton from "./ConnectWalletButton";
import TokenPricePill from "./TokenPricePill";

const MOBILE_CATEGORIES = [
  { slug: "politics", label: "Politics", Icon: IdentificationCard },
  { slug: "crypto", label: "Crypto", Icon: CurrencyBtc },
  { slug: "sports", label: "Sports", Icon: Trophy },
  { slug: "economy", label: "Economy", Icon: Bank },
  { slug: "geopolitics", label: "Geopolitics", Icon: Globe },
  { slug: "tech", label: "Tech", Icon: Cpu },
  { slug: "ai", label: "AI", Icon: Robot },
  { slug: "elections", label: "Elections", Icon: ChartLineUp },
  { slug: "trump", label: "Trump", Icon: Crown },
  { slug: "pop-culture", label: "Pop Culture", Icon: Confetti },
];

export default function Header({ search, setSearch }) {
  const navigate = useNavigate();
  const { user, isPro, signOut } = useAuth();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  // Close mobile menu when the page route changes
  React.useEffect(() => {
    if (!mobileOpen) return;
    const onClick = (e) => {
      // close when user taps a nav link
      if (e.target.closest("[data-mobile-nav-link]")) setMobileOpen(false);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [mobileOpen]);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 bg-zinc-950/70 backdrop-blur-xl border-b border-white/10"
    >
      <div className="mx-auto max-w-[1600px] px-4 sm:px-6 h-16 flex items-center gap-3 sm:gap-6">
        {/* Hamburger — mobile only */}
        <button
          data-testid="mobile-menu-btn"
          onClick={() => setMobileOpen((o) => !o)}
          aria-label="Menu"
          className="lg:hidden w-9 h-9 flex items-center justify-center border border-white/10 hover:border-edge/60 text-zinc-200"
        >
          {mobileOpen ? <X size={18} weight="bold" /> : <List size={18} weight="bold" />}
        </button>

        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group shrink-0">
          <div className="relative">
            <Brain size={26} weight="duotone" className="text-edge" />
            <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-edge animate-pulse-soft" />
          </div>
          <span className="font-display font-semibold text-base sm:text-lg tracking-tight">
            MFSMAI <span className="text-edge">Oracle</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-1 text-xs text-zinc-400 font-mono uppercase tracking-widest pl-2 border-l border-white/10 h-6">
          <Pulse size={14} className="text-profit animate-pulse-soft" />
          <span className="text-profit">LIVE</span>
          <span>· GAMMA</span>
        </div>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1 text-sm font-body">
          <NavLink to="/events" icon={Stack} label="Events" testid="nav-events" />
          <NavLink to="/edge" icon={Trophy} label="AI Edge" testid="nav-edge" />
          <NavLink to="/accuracy" icon={ChartLineUp} label="Track Record" testid="nav-accuracy" />
          <NavLink to="/about" icon={Info} label="About MFSMAI" testid="nav-about" />
        </nav>

        {/* Search — only visible on md+ to leave room for actions on phones */}
        <div className="flex-1 max-w-xl ml-auto hidden md:block">
          {setSearch && (
            <div className="relative">
              <MagnifyingGlass
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
              />
              <input
                data-testid="search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search markets…"
                className="w-full bg-zinc-900/60 border border-white/10 focus:border-edge/60 focus:outline-none rounded-sm pl-10 pr-3 py-2 text-sm font-body placeholder:text-zinc-600 transition-colors"
              />
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 ml-auto md:ml-0">
          <TokenPricePill variant="header" />

          {user && isPro && (
            <span
              data-testid="pro-pill"
              className="hidden sm:inline-flex items-center gap-1.5 px-2 py-1 border border-edge bg-edge/15 text-edge text-[10px] font-mono uppercase tracking-[0.22em]"
              title="MFSMAI Pro · token-verified"
            >
              <Lightning size={11} weight="fill" /> Pro
            </span>
          )}

          {user && <AlertsBell />}

          {!user && <ConnectWalletButton variant="header" />}

          {user && (
            <UserMenu user={user} isPro={isPro} onSignOut={signOut} onSettings={() => navigate("/settings")} onWatchlist={() => navigate("/watchlist")} />
          )}
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu-drawer"
          className="lg:hidden border-t border-white/10 bg-zinc-950/95 backdrop-blur-xl"
        >
          <nav className="px-4 py-3 grid grid-cols-1 gap-1">
            <MobileLink to="/" icon={HouseSimple} label="Dashboard" testid="mobile-nav-home" />
            <MobileLink to="/events" icon={Stack} label="Events" testid="mobile-nav-events" />
            <MobileLink to="/edge" icon={Trophy} label="AI Edge Leaderboard" testid="mobile-nav-edge" />
            <MobileLink to="/accuracy" icon={ChartLineUp} label="Track Record" testid="mobile-nav-accuracy" />
            <MobileLink to="/about" icon={Info} label="About MFSMAI" testid="mobile-nav-about" />

            {/* Categories — only show on Dashboard-style routes */}
            <div className="h-px bg-white/10 my-2" />
            <div className="px-3 pb-1.5 text-[10px] font-mono uppercase tracking-[0.28em] text-zinc-500">
              ◆ Categories
            </div>
            <div className="grid grid-cols-2 gap-1">
              {MOBILE_CATEGORIES.map((c) => (
                <Link
                  key={c.slug}
                  to={`/?cat=${c.slug}`}
                  data-mobile-nav-link
                  data-testid={`mobile-cat-${c.slug}`}
                  className="flex items-center gap-2 px-3 py-2.5 text-zinc-200 hover:text-white hover:bg-white/5 border border-transparent hover:border-edge/30 transition-colors"
                >
                  <c.Icon size={16} weight="duotone" className="text-edge shrink-0" />
                  <span className="font-body text-sm truncate">{c.label}</span>
                </Link>
              ))}
            </div>

            {user && (
              <>
                <div className="h-px bg-white/10 my-1" />
                <MobileLink to="/watchlist" icon={Sparkle} label="Watchlist" testid="mobile-nav-watchlist" />
                <MobileLink to="/alerts" icon={Pulse} label="Alerts" testid="mobile-nav-alerts" />
                <MobileLink to="/settings" icon={Gear} label="Settings" testid="mobile-nav-settings" />
              </>
            )}
          </nav>

          {/* Mobile search */}
          {setSearch && (
            <div className="px-4 pb-3">
              <div className="relative">
                <MagnifyingGlass
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
                />
                <input
                  data-testid="search-input-mobile"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search markets…"
                  className="w-full bg-zinc-900/60 border border-white/10 focus:border-edge/60 focus:outline-none rounded-sm pl-10 pr-3 py-2 text-sm font-body placeholder:text-zinc-600 transition-colors"
                />
              </div>
            </div>
          )}
        </div>
      )}
    </header>
  );
}

function NavLink({ to, icon: Icon, label, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="flex items-center gap-1.5 px-3 py-1.5 text-zinc-400 hover:text-white transition-colors"
    >
      <Icon size={15} weight="duotone" />
      {label}
    </Link>
  );
}

function MobileLink({ to, icon: Icon, label, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      data-mobile-nav-link
      className="flex items-center gap-3 px-3 py-3 text-zinc-200 hover:text-white hover:bg-white/5 border border-transparent hover:border-white/10 transition-colors"
    >
      <Icon size={18} weight="duotone" className="text-edge" />
      <span className="font-body text-sm">{label}</span>
    </Link>
  );
}

function UserMenu({ user, isPro, onSignOut, onSettings, onWatchlist }) {
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();
  return (
    <div className="relative">
      <button
        data-testid="user-menu-btn"
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-2 px-2 py-1 border transition-colors ${
          isPro ? "border-edge/60 hover:border-edge" : "border-white/15 hover:border-white/40"
        }`}
      >
        {user.picture ? (
          <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
        ) : (
          <div className={`w-6 h-6 flex items-center justify-center text-xs font-mono ${
            isPro ? "bg-edge text-black" : "bg-zinc-700 text-zinc-200"
          }`}>
            {(user.name || user.email || user.wallet_address || "?")[0].toUpperCase()}
          </div>
        )}
        <span className="hidden md:block text-sm font-body text-zinc-200 max-w-[120px] truncate">
          {user.name || user.email || (user.wallet_address && `${user.wallet_address.slice(0,6)}…${user.wallet_address.slice(-4)}`)}
        </span>
      </button>
      {open && (
        <div
          onMouseLeave={() => setOpen(false)}
          className="absolute right-0 top-full mt-1 w-64 bg-zinc-950 border border-white/15 z-50"
          data-testid="user-menu"
        >
          {user.wallet_address && (
            <div className="px-3 py-2 border-b border-white/10 bg-zinc-950/50">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[9px] font-mono uppercase tracking-[0.22em] text-zinc-500">Wallet</span>
                {isPro ? (
                  <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest text-edge">
                    <Lightning size={9} weight="fill" /> Pro
                  </span>
                ) : (
                  <span className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">Basic</span>
                )}
              </div>
              <div className="mt-1 flex items-center gap-1.5 text-xs font-mono text-zinc-300">
                <Wallet size={11} className="text-edge" />
                <span className="tabular-nums">{user.wallet_address.slice(0,6)}…{user.wallet_address.slice(-4)}</span>
              </div>
              <div className="text-[10px] font-mono text-zinc-500 mt-1 tabular-nums">
                {Number(user.pro_balance || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} MFSMAI held
              </div>
            </div>
          )}
          {!user.wallet_address && (
            <div className="px-3 py-2 border-b border-white/10">
              <ConnectWalletButton variant="header" />
            </div>
          )}
          <button
            data-testid="user-menu-watchlist"
            onClick={() => { setOpen(false); onWatchlist(); }}
            className="w-full text-left px-3 py-2 text-sm hover:bg-white/5 flex items-center gap-2 text-zinc-200"
          >
            <Sparkle size={14} /> Watchlist
          </button>
          <button
            data-testid="user-menu-settings"
            onClick={() => { setOpen(false); onSettings(); }}
            className="w-full text-left px-3 py-2 text-sm hover:bg-white/5 flex items-center gap-2 text-zinc-200"
          >
            <Gear size={14} /> Settings
          </button>
          <button
            data-testid="user-menu-about"
            onClick={() => { setOpen(false); navigate("/about"); }}
            className="w-full text-left px-3 py-2 text-sm hover:bg-white/5 flex items-center gap-2 text-zinc-200"
          >
            <Info size={14} /> About MFSMAI
          </button>
          <div className="border-t border-white/10" />
          <button
            data-testid="user-menu-signout"
            onClick={() => { setOpen(false); onSignOut(); }}
            className="w-full text-left px-3 py-2 text-sm hover:bg-white/5 flex items-center gap-2 text-loss"
          >
            <SignOut size={14} /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}
